import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import GeneralSeaAreaOperationalPhaseChart from './GeneralSeaAreaOperationalPhaseCountsChart';
import KeyMetrics from './KeyMetrics';
import LoadedCondition from './LoadedConditionChart';
import OperationalPhaseChart from './OperationalPhaseChart';
import SectorKpiMap from './SectorKpiMap';
import SpeedDistributionChart from './SpeedDistributionChart';
import ExpandableContainer from '../../../../shared/components/expandable-container/ExpandableContainer';
import { selectAnalysisDateRangeString } from '../../../date-range/dataRangeSelector';
import {
	summaryKeyMetricsInfoSelector,
	summaryPhaseCountsOfGeneralSeaAreasSelector,
	summaryPhasePercentagesSelector,
	summarySignalCountsOnSpeedRangesSelector,
} from '../../kpiSelectors';
import { greenActions, selectCohortVesselKeyMetricsDateBased } from '../../../green/greenSlice';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../date-range/slices/dateRangeSlice';
import { AppDispatch } from '../../../../core/store';
import { kpisActions } from '../../kpiSlice';
import LoadingSpinner from '../../../../shared/components/loading-spinner/LoadingSpinner';
import { TSummaryKeyMetrics } from '../../kpiTypes';
import { calculateAdjustedSpeedMetricsForSummary } from '../../kpisHelpers';

export interface ISectorKpiOverviewProps {
	title: string;
	imoList: number[];
	sectorId: number;
}

const SectorKpiOverview: React.FC<ISectorKpiOverviewProps> = ({ ...props }) => {
	const dateRangeString = useAppSelector(selectAnalysisDateRangeString);

	const sectorKeyMetrics = useAppSelector(summaryKeyMetricsInfoSelector);
	const sectorPhaseCountsOfGeneralSeaAreas = useAppSelector(summaryPhaseCountsOfGeneralSeaAreasSelector);

	const sectorPhasePercentages = useAppSelector(summaryPhasePercentagesSelector);
	const summarySignalCountsOnSpeedRanges = useAppSelector(summarySignalCountsOnSpeedRangesSelector);
	const summaryMetricsDateBased = useAppSelector(selectCohortVesselKeyMetricsDateBased);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	const dispatch = useAppDispatch<AppDispatch>();

	const [dataVersion, setDataVersion] = useState(0);
	const [adjustedKeyMetrics, setAdjustedKeyMetrics] = useState<TSummaryKeyMetrics>();

	useEffect(() => {
		setDataVersion((prev) => prev + 1);

		const adjustedMetrics = calculateAdjustedSpeedMetricsForSummary(sectorKeyMetrics, summarySignalCountsOnSpeedRanges);

		if (adjustedMetrics) {
			setAdjustedKeyMetrics(adjustedMetrics);
		}
	}, [
		sectorKeyMetrics,
		summarySignalCountsOnSpeedRanges,
		sectorPhaseCountsOfGeneralSeaAreas,
		sectorPhasePercentages,
		summaryMetricsDateBased,
	]);

	useEffect(() => {
		if (props.imoList.length !== 0) {
			const imoList = props.imoList;

			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;

			const startDate = analysisDateRange.dateRange?.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange?.end.toDate('UTC').toISOString();

			if (!startYearMonth || !endYearMonth || !startDate || !endDate) return;

			if (
				!sectorPhasePercentages.loading &&
				!sectorKeyMetrics.loading &&
				!sectorPhaseCountsOfGeneralSeaAreas.loading &&
				!summarySignalCountsOnSpeedRanges.loading
			) {
				dispatch(kpisActions.getSummaryKeyMetricsRequestAction({ imoList, startYearMonth, endYearMonth }));
				dispatch(kpisActions.getSummaryPhaseCountsOfGeneralSeaAreasRequestAction({ imoList, startYearMonth, endYearMonth }));
				dispatch(kpisActions.getSummaryPhasePercentagesRequestAction({ imoList, startYearMonth, endYearMonth }));
				dispatch(kpisActions.getSummarySignalCountsOnSpeedRangesRequestAction({ imoList, startYearMonth, endYearMonth }));
				dispatch(
					greenActions.getCohortVesselKeyMetricsDateBasedRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }),
				);
				dispatch(
					kpisActions.getSectorActivityCoverageRequestAction({
						imoList,
						startYearMonth,
						endYearMonth,
						startDate,
						endDate,
					}),
				);
			}
		}
	}, [dispatch, props.imoList, analysisDateRange]);

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-col gap-2 w-full h-full">
			{sectorKeyMetrics.loading ? (
				<div className="flex w-full h-full justify-center items-center">
					<LoadingSpinner size="md" spinnerClassName="p-2" />
				</div>
			) : !sectorKeyMetrics?.sectorListKeyMetrics?.length ? (
				<div className="flex w-full h-full justify-center">
					<span>No signals found!</span>
				</div>
			) : (
				<>
					<div className="w-full flex-col">
						{/* <span className="font-bold text-msi-midblue-color-500">{props.title} | </span> */}
						<span className="font-bold">Operational Profile : </span>
						<span>{dateRangeString}</span>

						{/* <h2 className="text-xl font-bold">{props.title}</h2> */}
					</div>
					<div className="flex flex-col xl:flex-row gap-4 w-full h-full">
						<div className="flex flex-col w-2/5 h-full gap-4">
							<div className="flex flex-row h-full gap-2">
								<div className="border border-msi-darkgrey-color-300 rounded w-2/3 h-full">
									{!sectorPhasePercentages ||
									sectorPhasePercentages.loading ||
									!sectorPhasePercentages.sectorListPhasePercentages ||
									sectorPhasePercentages.sectorListPhasePercentages.length === 0 ? null : (
										<ExpandableContainer title="Operational Phase" panelOffsets={panelOffset}>
											<OperationalPhaseChart
												key={`operational-phase-${dataVersion}`}
												chartKey={sectorPhasePercentages.sectorListPhasePercentages[0].sectorId!}
												phasePercentagesData={sectorPhasePercentages.sectorListPhasePercentages[0].dataList!}
											/>
										</ExpandableContainer>
									)}
								</div>
								<div className="border border-msi-darkgrey-color-300 rounded w-1/3 h-full">
									{!adjustedKeyMetrics ||
									adjustedKeyMetrics.loading ||
									!adjustedKeyMetrics.sectorListKeyMetrics ||
									adjustedKeyMetrics.sectorListKeyMetrics.length === 0 ||
									!adjustedKeyMetrics.sectorListKeyMetrics[0].data ? null : (
										<ExpandableContainer title="% Ballast" panelOffsets={panelOffset}>
											<LoadedCondition
												key={`loaded-condition-${dataVersion}`}
												chartKey={adjustedKeyMetrics.sectorListKeyMetrics[0].sectorId!}
												keyMetricsData={adjustedKeyMetrics.sectorListKeyMetrics[0].data!}
											/>
										</ExpandableContainer>
									)}
								</div>
							</div>
							<div className="flex flex-row w-full h-full border border-msi-darkgrey-color-300 rounded">
								{!sectorPhaseCountsOfGeneralSeaAreas ||
								sectorPhaseCountsOfGeneralSeaAreas.loading ||
								!sectorPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas ||
								sectorPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas.length === 0 ? null : (
									<ExpandableContainer title="Geographical Distribution (hours)" panelOffsets={panelOffset}>
										<GeneralSeaAreaOperationalPhaseChart
											key={`geographical-distribution-${dataVersion}`}
											chartKey={sectorPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas[0].sectorId!}
											phaseCountsOfGeneralSeaAreasData={
												sectorPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas[0].dataList!
											}
										/>
									</ExpandableContainer>
								)}
							</div>
							<div className="flex flex-row w-full h-full border border-msi-darkgrey-color-300 rounded">
								{!summarySignalCountsOnSpeedRanges ||
								summarySignalCountsOnSpeedRanges.loading ||
								!summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges ||
								summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges.length === 0 ? null : (
									<ExpandableContainer title="Speed Distribution (knots)" panelOffsets={panelOffset}>
										<SpeedDistributionChart
											key={`speed-distribution-${dataVersion}`}
											chartKey={summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges[0].sectorId!}
											signalCountsOnSpeedRangesData={
												summarySignalCountsOnSpeedRanges.sectorListSignalCountsOnSpeedRanges[0].dataList!
											}
										/>
									</ExpandableContainer>
								)}
							</div>
						</div>

						<div className="w-3/5 h-full">
							<div className="flex flex-col h-full w-full border border-msi-darkgrey-color-300 rounded  p-1">
								<ExpandableContainer panelOffsets={panelOffset} hideTitleBar reverseIconColor>
									<SectorKpiMap imoList={props.imoList} />
								</ExpandableContainer>
							</div>
						</div>

						<div className="w-1/5 h-full border border-msi-darkgrey-color-300 rounded p-2">
							{!adjustedKeyMetrics ||
							adjustedKeyMetrics.loading ||
							!adjustedKeyMetrics.sectorListKeyMetrics ||
							adjustedKeyMetrics.sectorListKeyMetrics.length === 0 ? null : (
								<KeyMetrics
									key={`key-metrics-${dataVersion}`}
									keyMetricsData={adjustedKeyMetrics?.sectorListKeyMetrics?.[0]?.data || null}
									keyMetricsDateBased={
										(summaryMetricsDateBased &&
											summaryMetricsDateBased.cohortVesselKeyMetricsDateBasedData &&
											summaryMetricsDateBased.cohortVesselKeyMetricsDateBasedData.length > 0 &&
											summaryMetricsDateBased.cohortVesselKeyMetricsDateBasedData[0]) ||
										null
									}
								/>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default SectorKpiOverview;
